import type { LeaveSummaryDto } from '../../../api/hr';
import type { GroupedSelectGroup, GroupedSelectOption } from '../../../components/shared/GroupedSelect';
import { DEFAULT_DAILY_WORK_MINUTES, formatLeaveAmount } from '../../../lib/leave-amount';

/**
 * 근태 신청 「휴가 유형」 선택창의 묶음 · 잔여 · 막힘 (2026-09-15 · 대시보드 시안 v2 V8).
 *
 * 근거: web-plan §4 규칙 D9 · D10 · 관제 결정 Q-11(잔여 없는 유형 막기 기준).
 *
 * ★★★ **묶음은 서버의 `deductsAnnual` 과 같아야 한다** (`apps/chat-server/src/modules/hr/leave-type-attrs.ts:47-71`).
 *     연차 · 반차 · 반반차 = 연차에서 빠짐 · 대체휴가 = 따로 쌓임 · 병가 · 경조 = 사유가 있음.
 *     서버처럼 **모르는 코드는 연차에서 빠지는 것**으로 본다. 서버 카탈로그(DR2)가 오면 이 사본을 버린다.
 * ★★★ **못 재면 막지 않는다.** 부여를 모르는 사람(`available` 이 null)은 막지 않고 잔여를 「—」로 둔다 —
 *     서버 `checkAnnualSufficiency` 가 같은 원칙이다(「모른다」를 「없다」로 바꾸지 않는다).
 * ★★ 막는 기준(Q-11): 연차 계열은 `available`(잔여 − 신청중) · 대체휴가는 `remaining` 이 **그 유형의 한 번 몫**보다
 *    적으면 막는다 — 날짜 범위는 하루, 반차는 소정의 절반, 반반차는 1/4. 사유가 있는 휴가는 막지 않는다.
 * ⚠ 대체휴가는 서버가 아직 잔여를 검사하지 않는다(DR12) — 웹이 먼저 막는다.
 * ⚠ 샘플 숫자 없음 — 값은 전부 서버 요약에서 온다.
 */

export interface LeaveTypeItem {
  key: string;
  label: string;
  /** 하루의 몫(반차 0.5 · 반반차 0.25). 날짜 범위로 쓰는 종류는 null. */
  fraction: number | null;
}

/**
 * 묶음 가름은 **`lib/leave-type.ts` 한 곳**에서 온다 (2026-09-16 · 사본 합치기).
 * ★ 옛 import 경로를 깨지 않으려고 그대로 재수출한다 — 규칙은 그 파일과 같다
 *   (모르는 코드는 연차 쪽 · 서버 `deductsAnnual` 과 같은 가름).
 */
import { leaveBucket, type LeaveBucket } from '../../../lib/leave-type';

export { leaveBucket };
export type { LeaveBucket };

function dailyOf(summary: LeaveSummaryDto | null | undefined): number {
  const d = summary?.dailyMinutes;
  return typeof d === 'number' && d > 0 ? d : DEFAULT_DAILY_WORK_MINUTES;
}

/** 연차에서 더 낼 수 있는 분 — 모르면 null. 분 칸이 없는 옛 서버는 일수 × 소정. */
export function annualAvailableMinutes(summary: LeaveSummaryDto | null | undefined): number | null {
  if (!summary) return null;
  const m = summary.annualMinutes;
  if (m) return m.available ?? m.remaining ?? null;
  const days = summary.annual.available ?? summary.annual.remaining;
  return days == null ? null : Math.round(days * dailyOf(summary));
}

/** 대체휴가 남은 분 — 모르면 null. */
export function substituteRemainingMinutes(summary: LeaveSummaryDto | null | undefined): number | null {
  if (!summary) return null;
  if (summary.substituteMinutes) return summary.substituteMinutes.remaining;
  return summary.substitute ? Math.round(summary.substitute.remaining * dailyOf(summary)) : null;
}

function amountText(minutes: number, daily: number): string {
  return minutes <= 0 ? '0일' : formatLeaveAmount(minutes, daily);
}

function unitTag(item: LeaveTypeItem): string {
  return item.fraction == null ? '날짜 범위' : `${item.fraction}일`;
}

/** 이 유형을 한 번 쓰는 데 드는 분. */
function needMinutes(item: LeaveTypeItem, daily: number): number {
  return item.fraction == null ? daily : Math.round(daily * item.fraction);
}

const GROUP_LABEL: Record<LeaveBucket, string> = {
  annual: '연차에서 빠지는 휴가',
  accrued: '따로 쌓이는 휴가',
  reason: '사유가 있는 휴가 · 연차에서 빠지지 않음',
};

export function buildLeaveTypeGroups(
  types: readonly LeaveTypeItem[],
  summary: LeaveSummaryDto | null | undefined,
): GroupedSelectGroup[] {
  const daily = dailyOf(summary);
  const annual = annualAvailableMinutes(summary);
  const sub = substituteRemainingMinutes(summary);

  const toOption = (t: LeaveTypeItem): GroupedSelectOption => {
    const bucket = leaveBucket(t.key);
    const balance = bucket === 'annual' ? annual : bucket === 'accrued' ? sub : null;
    const base: GroupedSelectOption = { value: t.key, label: t.label, tag: unitTag(t), meta: balance == null ? '—' : amountText(balance, daily) };
    if (balance == null) return base;
    const need = needMinutes(t, daily);
    if (balance >= need) return base;
    const what = bucket === 'annual' ? '연차' : '보상휴가';
    return {
      ...base,
      disabled: true,
      disabledReason: balance <= 0 ? '잔여 0일' : `남은 ${what}가 ${formatLeaveAmount(need, daily)}보다 적습니다`,
    };
  };

  const order: LeaveBucket[] = ['annual', 'accrued', 'reason'];
  return order
    .map((bucket) => ({
      key: bucket,
      label: bucket === 'annual' && annual != null ? `${GROUP_LABEL.annual} · ${amountText(annual, daily)} 남음` : GROUP_LABEL[bucket],
      options: types.filter((t) => leaveBucket(t.key) === bucket).map(toOption),
    }))
    .filter((g) => g.options.length > 0);
}

function findOption(groups: readonly GroupedSelectGroup[], value: string): GroupedSelectOption | null {
  for (const g of groups) {
    const o = g.options.find((x) => x.value === value);
    if (o) return o;
  }
  return null;
}

/** 트리거 오른쪽 요약 — 「잔여 7.5일 · 날짜 범위」 · 잔여를 모르면 단위만. */
export function leaveTypeTriggerSummary(groups: readonly GroupedSelectGroup[], value: string): string | undefined {
  const o = findOption(groups, value);
  if (!o) return undefined;
  return o.meta && o.meta !== '—' ? `잔여 ${o.meta} · ${o.tag}` : o.tag;
}

/** 고른 유형이 지금 막혔나 — 신청 단추를 끈다(고를 수 없는 것이 이미 골라져 있는 경우). */
export function isLeaveTypeBlocked(groups: readonly GroupedSelectGroup[], value: string): boolean {
  return findOption(groups, value)?.disabled === true;
}

/** 창 위 한 줄 — 「잔여 연차 7.5일 / 15일 · 대체휴가 1일 · 결재 대기 1일」. 요약이 없으면 null. */
export function leaveBalanceLine(summary: LeaveSummaryDto | null | undefined): string | null {
  if (!summary) return null;
  const daily = dailyOf(summary);
  const parts: string[] = [];
  const annual = annualAvailableMinutes(summary);
  const total = summary.annual.total;
  parts.push(`잔여 연차 ${annual == null ? '—' : amountText(annual, daily)}${total == null ? '' : ` / ${total}일`}`);
  const sub = substituteRemainingMinutes(summary);
  if (sub != null) parts.push(`보상휴가 ${amountText(sub, daily)}`);
  const pending = summary.annualMinutes?.pending ?? Math.round((summary.annual.pending ?? 0) * daily);
  if (pending > 0) parts.push(`결재 대기 ${formatLeaveAmount(pending, daily)}`);
  return parts.join(' · ');
}
